import { useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Form, Input, Button, Select, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Redirect } from 'react-router-dom';
import FormItem from 'components/form/FormItem';
import { AppDispatch } from 'redux/store';
import styles from './ProjectSetupPage.module.less';
import { createProject } from 'redux/reducers/projectsSlice';
import OmniNotification from 'components/OmniNotification';
import { userSelector } from 'redux/selectors/userSelector';
import { getUserInfo } from 'redux/reducers/authenticationSlice';
import {
  acceptInvitation,
  getInvitationsForUser,
} from 'redux/reducers/collaboratorSlice';
import { ProjectInvitationDto } from '../../api';
import { useAuth0 } from '@auth0/auth0-react';

const { Option } = Select;

interface FormValues {
  name: string;
}

const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    name: yup
      .string()
      .required(
        t('validation.required', {
          fieldName: t('ProjectSetupPage.Name'),
        }),
      )
      .max(
        50,
        t('validation.maxLength', {
          fieldName: t('ProjectSetupPage.Name'),
          count: 50,
        }),
      ),
  });

export default function ProjectSetupPage() {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const user = useSelector(userSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);
  const [userInvitations, setUserInvitations] =
    useState<ProjectInvitationDto[]>();
  const [showInvitationsSelect, setShowInvitationsSelect] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState(0);
  const dispatch: AppDispatch = useDispatch();
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const { control, handleSubmit, formState } = useForm<FormValues>({
    mode: 'all',
    criteriaMode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
    },
  });
  const { isSubmitting } = formState;
  useEffect(() => {
    const getInvtitations = async () => {
      setIsLoading(true);
      const result = await dispatch(getInvitationsForUser(user?.email));
      if (getInvitationsForUser.fulfilled.match(result)) {
        if (result.payload.length) {
          setUserInvitations(result.payload);
          setShowInvitationsSelect(true);
        }
      } else {
        OmniNotification.error({
          message: t('notification.error.title'),
          description: t('api.defaultError'),
        });
      }
      setIsLoading(false);
    };

    if (!user?.firstName) {
      logout({returnTo: window.location.origin});
    }
    getInvtitations();
  }, []);


  const onSubmit = async (values: FormValues) => {
    const { name } = values;
    const data = {
      name,
    };

    const result = await dispatch(createProject(data.name));
    if (createProject.fulfilled.match(result)) {
      OmniNotification.success({
        message: t('ProjectPage.Notification.Header'),
        description: t('ProjectPage.Notification.Message', {
          project_name: name,
        }),
      });
      setSetupSuccess(true);
    }
  };

  const onAcceptInvitation = async () => {
    setIsAcceptingInvitation(true);
    const inviteId =
      userInvitations?.length === 1
        ? userInvitations[0].invitationId
        : selectedInvitationId;
    const result = await dispatch(acceptInvitation(inviteId));

    if (acceptInvitation.fulfilled.match(result)) {
      setSetupSuccess(true);
    } else {
      OmniNotification.error({
        message: t('notification.error.title'),
        description: t('api.defaultError'),
      });
    }
    setIsAcceptingInvitation(false);
  };

  const renderSingleInvitationView = () => {
    const invitation = userInvitations && userInvitations[0];
    return (
      <div
        data-testid="single_invitation_form"
        className={styles.projectInvitations}
      >
        <h2>
          {t('ProjectSetupPage.Description.SingleInvitation', {
            inviterName: invitation?.inviterName,
            projectName: invitation?.projectName,
          })}
        </h2>

        <Button
          loading={isAcceptingInvitation}
          type="primary"
          size="large"
          className={styles.joinButton}
          onClick={onAcceptInvitation}
        >
          {t('ProjectSetupPage.Button.JoinProject')}
        </Button>

        <a
          data-testid="project_invitation_switch"
          className={styles.switcher}
          onClick={() => setShowInvitationsSelect(false)}
        >
          {t('ProjectSetupPage.Switch.CreateProjectView')}
        </a>
      </div>
    );
  };

  const renderMultiInvitationView = () => (
    <div className={styles.projectInvitations}>
      <h2>{t('ProjectSetupPage.Description.MultiInvitation')}</h2>
      <div>
        <Select
          data-testid="multi_invitation_select"
          className={styles.invitationSelect}
          placeholder={t('ProjectSetupPage.MultiInvitation.SelectPlaceholder')}
          size="large"
          onSelect={(value: number) => {
            setSelectedInvitationId(value);
          }}
        >
          {userInvitations?.map(
            (invite) =>
              invite?.invitationId && (
                <Option key={invite.invitationId} value={invite.invitationId}>
                  {t('ProjectSetupPage.MultiInvitation.Dropdown', {
                    projectName: invite.projectName,
                    inviterName: invite.inviterName,
                  })}
                </Option>
              ),
          )}
        </Select>

        <Button
          loading={isAcceptingInvitation}
          type="primary"
          size="large"
          className={styles.joinMultipleButton}
          onClick={onAcceptInvitation}
        >
          {t('ProjectSetupPage.Button.JoinProject')}
        </Button>
      </div>

      <a
        data-testid="project_invitation_switch"
        className={styles.switcher}
        onClick={() => setShowInvitationsSelect(false)}
      >
        {t('ProjectSetupPage.Switch.CreateProjectView')}
      </a>
    </div>
  );

  const renderCreateProjectView = () => (
    <>
      <h2>{t('ProjectSetupPage.Description')}</h2>

      <Form
        data-testid="initial-project-setup-form"
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        className={styles.setup}
      >
        <FormItem
          name="name"
          label={t('ProjectSetupPage.Name')}
          control={control}
          render={({ field }) => (
            <Input className={styles.setupInput} size="large" {...field} />
          )}
          className={styles.setupFormItem}
        />

        <Button
          loading={isSubmitting}
          data-testid="submit-btn"
          type="primary"
          htmlType="submit"
          size="large"
          className={styles.createProjectButton}
        >
          {t('ProjectSetupPage.Button')}
        </Button>

        {userInvitations?.length && (
          <a onClick={() => setShowInvitationsSelect(true)}>
            {t('ProjectSetupPage.Switch.AcceptInvitationView')}
          </a>
        )}
      </Form>
    </>
  );

  if (isLoading) return <Spin />;

  return (
    <>
      {setupSuccess && <Redirect to="/projects" />}

      <div className={styles.projectSetup}>
        <div className={styles.header}>
          <h1>
            &#128075;{t('common.welcome')}, <b>{user?.firstName}</b>
          </h1>
        </div>
        <div className={styles.projectSetupFormContainer}>
          {showInvitationsSelect
            ? userInvitations?.length === 1
              ? renderSingleInvitationView()
              : renderMultiInvitationView()
            : renderCreateProjectView()}
        </div>
      </div>
    </>
  );
}
